/**
 * useRealtimeSync — Conexión SSE con backend para sync en tiempo real.
 *
 * Eventos que escucha el servidor (todos publicados al window.dispatchEvent):
 *   - "realtime:tarea-updated"   detail: { id, tarea, actorId, changes }
 *   - "realtime:tarea-created"   detail: { id, tarea, actorId }
 *   - "realtime:tarea-deleted"   detail: { id, actorId }
 *   - "realtime:comment-created" detail: { id, entidad, entidad_id, autor_id, ... }
 *
 * Comportamiento:
 *   - Abre EventSource a /api/events?token=<firebaseToken>
 *   - Reconecta con backoff exponencial (1s → 2s → 5s → 10s → 30s, max)
 *   - Pausa cuando la tab está en background (visibilitychange)
 *   - Refresca token cuando expira (token expira a los 60min)
 *
 * Uso (un solo lugar, en App.jsx):
 *   useRealtimeSync();
 *
 * Y cualquier componente puede escuchar:
 *   useEffect(() => {
 *     const onUpdate = (e) => { ... };
 *     window.addEventListener("realtime:tarea-updated", onUpdate);
 *     return () => window.removeEventListener("realtime:tarea-updated", onUpdate);
 *   }, []);
 */
import { useEffect, useRef } from "react";
import { getAuth } from "firebase/auth";

const BASE_URL = import.meta.env.VITE_API_URL || "";

// Backoff: 1s, 2s, 5s, 10s, 30s (max)
const BACKOFF_MS = [1000, 2000, 5000, 10_000, 30_000];

// Tipos de eventos que el servidor emite (deben matchear los emit() del backend)
const EVENT_TYPES = [
  "hello",
  "tarea.updated",
  "tarea.created",
  "tarea.deleted",
  "comment.created",
  "grupo.updated",
  "cliente.updated",
  "notif.new",
  "server-shutdown",
];

export function useRealtimeSync() {
  const esRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef(null);
  const isPaused = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("EventSource" in window)) {
      console.warn("[realtime] EventSource no soportado en este browser");
      return;
    }

    async function connect() {
      if (isPaused.current) return;
      try { esRef.current?.close(); } catch {}

      const user = getAuth().currentUser;
      if (!user) {
        // Sin auth — reintentar en 5s
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(connect, 5000);
        return;
      }

      const token = await user.getIdToken().catch(() => null);
      if (!token) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(connect, 5000);
        return;
      }

      // Se pide un ticket de 60 s para no poner el token de Firebase en la URL (quedaba en los logs); si no se puede, se usa el token como antes.
      let url = `${BASE_URL}/api/events?token=${encodeURIComponent(token)}`;
      try {
        const r = await fetch(`${BASE_URL}/api/events/ticket`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        const j = r.ok ? await r.json().catch(() => null) : null;
        if (j?.ticket) url = `${BASE_URL}/api/events?ticket=${encodeURIComponent(j.ticket)}`;
      } catch { /* sin ticket: se conecta con el token */ }
      const es = new EventSource(url);
      esRef.current = es;

      // Listener genérico para cada tipo de evento
      for (const type of EVENT_TYPES) {
        es.addEventListener(type, (e) => {
          try {
            const data = JSON.parse(e.data);
            // Publicar como CustomEvent en window para que cualquier componente escuche
            const eventName = `realtime:${type.replace(".", "-")}`;
            window.dispatchEvent(new CustomEvent(eventName, { detail: data?.payload || data }));
            if (type === "hello") {
              console.info("[realtime] conectado", data);
              reconnectAttempt.current = 0;
            }
          } catch (err) {
            console.warn("[realtime] parse error:", err.message);
          }
        });
      }

      es.onerror = (e) => {
        // Cerrar y reconectar con backoff
        try { es.close(); } catch {}
        if (isPaused.current) return;
        const delay = BACKOFF_MS[Math.min(reconnectAttempt.current, BACKOFF_MS.length - 1)];
        reconnectAttempt.current++;
        console.warn(`[realtime] reconnect en ${delay}ms (intento ${reconnectAttempt.current})`);
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(connect, delay);
      };
    }

    // Pause/Resume basado en visibilidad
    function handleVisibility() {
      if (document.hidden) {
        isPaused.current = true;
        try { esRef.current?.close(); } catch {}
        clearTimeout(reconnectTimer.current);
      } else {
        isPaused.current = false;
        reconnectAttempt.current = 0;
        connect();
      }
    }

    // Refresco de token cada 50min (token expira a los 60min)
    const tokenRefresh = setInterval(() => {
      if (!isPaused.current) connect();
    }, 50 * 60 * 1000);

    connect();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      try { esRef.current?.close(); } catch {}
      clearTimeout(reconnectTimer.current);
      clearInterval(tokenRefresh);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);
}
